/**
 * Online play from the game's side: who this browser is (a random player id, a secret key that
 * proves it, and a display name) and the site's rooms and leaderboard API (functions/api).
 */
import type { Replay, Side } from '../engine';
import { cleanName, nameProblem, usableName, type NameProblem } from '../online/names';
import { PLAYER_ID, PLAYER_KEY, ROOM_ID, type RoomInfo } from '../online/protocol';
import { currentStore, readJson, writeJson, type KeyValueStore } from './storage';

export interface OnlineIdentity {
  readonly player: string;
  readonly key: string;
  /** Empty until the player picks a name. */
  readonly name: string;
}

const KEY = 'online.v1';

const randomHex = (bytes: number): string =>
  [...crypto.getRandomValues(new Uint8Array(bytes))]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

function validate(v: unknown): OnlineIdentity | null {
  if (typeof v !== 'object' || v === null) return null;
  const o = v as Record<string, unknown>;
  if (typeof o.player !== 'string' || !PLAYER_ID.test(o.player)) return null;
  if (typeof o.key !== 'string' || !PLAYER_KEY.test(o.key)) return null;
  // A name no longer allowed is dropped: the player is asked for another.
  return { player: o.player, key: o.key, name: usableName(o.name) ?? '' };
}

/**
 * The identity storage refused to keep (full, or blocked), and why: it stays this session's
 * identity, the same every time it is asked for, until the page is closed. The online screens
 * say so before a game (identityNotSaved), since a reload then loses the player's seats and
 * rating.
 */
let unsaved: {
  readonly store: KeyValueStore;
  readonly identity: OnlineIdentity;
  readonly error: string;
} | null = null;

/** Stores an identity, or keeps it for the session when storage refuses it. */
function keep(identity: OnlineIdentity): void {
  const error = writeJson(KEY, identity);
  unsaved = error ? { store: currentStore(), identity, error } : null;
}

/** This browser's online identity, created on first use. */
export function loadIdentity(): OnlineIdentity {
  if (unsaved?.store === currentStore()) return unsaved.identity;
  const found = readJson(KEY, validate);
  if (found) return found;
  const created: OnlineIdentity = { player: randomHex(12), key: randomHex(32), name: '' };
  keep(created);
  return created;
}

/** Why this browser could not store its online identity, if it could not. */
export const identityNotSaved = (): string | null =>
  unsaved?.store === currentStore() ? unsaved.error : null;

/** Why a name cannot be saved: not made of letters and digits, or not allowed. */
export type NameError = 'invalid' | NameProblem;

/** Saves the player's name, or says why it cannot be used (src/online/names.ts). */
export function saveName(name: string): { identity: OnlineIdentity } | { error: NameError } {
  const clean = cleanName(name);
  if (!clean) return { error: 'invalid' };
  const problem = nameProblem(clean);
  if (problem) return { error: problem };
  const identity = { ...loadIdentity(), name: clean };
  keep(identity);
  return { identity };
}

/** A link that opens a room (a game to join or watch, or a shared replay). */
export function roomLink(id: string): string {
  const url = new URL(location.href);
  url.search = `?room=${id}`;
  url.hash = '';
  return url.toString();
}

/** The room id in a link or typed code, if any. */
export function roomIdFrom(text: string): string | null {
  const trimmed = text.trim();
  if (ROOM_ID.test(trimmed.toLowerCase())) return trimmed.toLowerCase();
  try {
    const id = new URL(trimmed).searchParams.get('room');
    return id && ROOM_ID.test(id) ? id : null;
  } catch {
    return null;
  }
}

async function request<T>(
  path: string,
  init?: RequestInit,
): Promise<{ status: number; body: T | null }> {
  try {
    const res = await fetch(path, init);
    const body = res.headers.get('Content-Type')?.includes('application/json')
      ? ((await res.json()) as T)
      : null;
    return { status: res.status, body };
  } catch {
    return { status: 0, body: null };
  }
}

export interface OnlineStatus {
  readonly online: boolean;
  readonly leaderboard: boolean;
}

/** Whether this site hosts online games; false offline and in local development. */
export async function onlineStatus(): Promise<OnlineStatus> {
  const r = await request<OnlineStatus>('api/online');
  return r.status === 200 && r.body
    ? { online: r.body.online === true, leaderboard: r.body.leaderboard === true }
    : { online: false, leaderboard: false };
}

export type Created = { readonly id: string } | { readonly error: string };

const post = async (body: unknown): Promise<Created> => {
  const r = await request<{ id?: string; error?: string }>('api/rooms', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (r.status === 201 && r.body?.id) return { id: r.body.id };
  if (r.status === 0) return { error: 'offline' };
  return { error: r.body?.error ?? `error ${r.status}` };
};

export const createGame = (preset: string): Promise<Created> => post({ kind: 'game', preset });

export const shareReplay = (
  replay: Replay,
  names: Readonly<Record<Side, string>>,
): Promise<Created> => post({ kind: 'replay', replay, names });

/** A room's public info: null when there is no such room, 'unavailable' without the server. */
export async function fetchRoom(id: string): Promise<RoomInfo | null | 'unavailable'> {
  const r = await request<RoomInfo>(`api/rooms/${id}`);
  if (r.status === 200 && r.body) return r.body;
  return r.status === 404 ? null : 'unavailable';
}

export async function fetchRoomReplay(
  id: string,
): Promise<{ readonly replay: Replay; readonly room: RoomInfo } | null> {
  const r = await request<{ replay: Replay; room: RoomInfo }>(`api/rooms/${id}/replay`);
  return r.status === 200 && r.body ? r.body : null;
}

export interface LeaderboardRow {
  readonly rank: number;
  readonly name: string;
  readonly rating: number;
  readonly games: number;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
}

export interface Leaderboard {
  readonly players: readonly LeaderboardRow[];
  readonly me: LeaderboardRow | null;
}

export async function fetchLeaderboard(player?: string): Promise<Leaderboard | null> {
  const r = await request<Leaderboard>(
    `api/leaderboard${player ? `?player=${encodeURIComponent(player)}` : ''}`,
  );
  return r.status === 200 && r.body ? r.body : null;
}

/** The WebSocket address of a room, next to the page. */
export function roomSocketUrl(id: string): string {
  const url = new URL(`api/rooms/${id}/ws`, location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}
